import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Rocket, Check, Zap, Palette, ShieldCheck, ArrowRight, Sparkles,
  Scissors, Dumbbell, Wrench, Stethoscope, Camera, Cake, Briefcase, Home,
  Car, GraduationCap, PawPrint, Brush, Play,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import heroPeople from "@/assets/hero-people.jpg";
import heroVideo from "@/assets/hero-video.mp4";
import heroVideoPoster from "@/assets/hero-video-poster.jpg";
import sierraHeadshot from "@/assets/sierra-headshot-new.jpg";
import sierraStairs from "@/assets/sierra-stairs.jpg";
import portfolio1 from "@/assets/portfolio-1.jpg";
import portfolio2 from "@/assets/portfolio-2.jpg";
import portfolio3 from "@/assets/portfolio-3.jpg";
import portfolio4 from "@/assets/portfolio-4.jpg";
import portfolioSpeakingAuthor from "@/assets/portfolio/speaking-author-sharp.jpg";
import portfolioSeniorTransportation from "@/assets/portfolio/senior-transportation.png";
import portfolioCatering from "@/assets/portfolio/catering.png";
import portfolioDogGrooming from "@/assets/portfolio/dog-grooming.png";
import portfolioFinancialAdvisor from "@/assets/portfolio/financial-advisor.png";
import portfolioPhysicalTherapist from "@/assets/portfolio/physical-therapist.png";
import portfolioMedSpa from "@/assets/portfolio/medspa.png";
import portfolioCoffeeShop from "@/assets/portfolio/coffee-shop.png";
import portfolioLandscaping from "@/assets/portfolio/landscaping.png";
import portfolioAccounting from "@/assets/portfolio/accounting.png";
import portfolioKeynotes from "@/assets/portfolio/keynotes.png";
import portfolioHairSalon from "@/assets/portfolio/hair-salon.png";
import avatarElaine from "@/assets/testimonials/avatar-elaine-B3rNOmMI.png";
import avatarTarrn from "@/assets/testimonials/avatar-tarrn-DBz18DZ_.png";
import avatarLissa from "@/assets/testimonials/avatar-lissa-B28uOaHv.png";
import avatarAhriana from "@/assets/testimonials/avatar-ahriana-BmR4_NC6.png";
import avatarCarrie from "@/assets/testimonials/avatar-carrie-DtFoRfBZ.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/everybusiness")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { title: "Three Day Launch — Custom Websites in 3 Days" },
      { name: "description", content: "Custom (no-template) websites for small business owners. Designed and launched in 3 days. Never a template." },
      { property: "og:title", content: "Three Day Launch — Custom Websites in 3 Days" },
      { property: "og:description", content: "Custom (no-template) websites for small business owners. Designed and launched in 3 days. Never a template." },
      { property: "og:url", content: "https://threedaylaunch.com/" },
      { name: "twitter:title", content: "Three Day Launch — Custom Websites in 3 Days" },
      { name: "twitter:description", content: "Custom (no-template) websites for small business owners. Designed and launched in 3 days. Never a template." },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Three Day Launch — Custom Websites in 3 Days",
          url: "https://threedaylaunch.com/",
          description: "Custom (no-template) websites for small business owners. Designed and launched in 3 days.",
          publisher: { "@type": "Organization", name: "Three Day Launch" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What types of websites do you design?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We've launched hundreds of custom websites for service-based businesses across industries — consulting, finance, dog grooming, interior design, and more. We do not build e-commerce websites.",
              },
            },
            {
              "@type": "Question",
              name: "Do you really launch in 3 days?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Day one is a kickoff Zoom call. Day two is design day with feedback rounds. Day three we connect your domain and set up hosting so you go live.",
              },
            },
            {
              "@type": "Question",
              name: "What about my website domain?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "You purchase your domain (like mybusiness.com) separately from a registrar like GoDaddy for about $20 per year. We connect it to your new website for you.",
              },
            },
            {
              "@type": "Question",
              name: "What about hosting?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We take care of hosting setup as part of your launch so you never have to worry about keeping your site online.",
              },
            },
            {
              "@type": "Question",
              name: "Do you help with ongoing updates and maintenance?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. After your site is designed, you can choose from ongoing website care plans for hosting and edits.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

const bookingUrl = "/book";

const businessTypes = [
  { icon: Scissors, name: "Salons & Barbers" },
  { icon: Dumbbell, name: "Gyms & Trainers" },
  { icon: Wrench, name: "Plumbers & Electricians" },
  { icon: Stethoscope, name: "Clinics & Therapists" },
  { icon: Camera, name: "Photographers" },
  { icon: Cake, name: "Bakers & Caterers" },
  { icon: Briefcase, name: "Consultants & Coaches" },
  { icon: Home, name: "Real Estate Agents" },
  { icon: Car, name: "Auto Detailers" },
  { icon: GraduationCap, name: "Tutors & Instructors" },
  { icon: PawPrint, name: "Pet Groomers" },
  { icon: Brush, name: "Cleaners & Painters" },
];

function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <Hero />
      <LogoStrip />
      <DollarSection />
      {/* <FounderVideo /> */}
      <WhoFor />
      <Portfolio />
      <HowAffordable />
      <Process />
      <ThenWhat />
      <Testimonials />
      
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="mx-auto max-w-7xl px-6 py-5 flex flex-wrap items-center justify-between gap-3">
        <a href="#" className="flex w-full sm:w-auto items-center gap-2 text-white">
          <div className="h-9 w-9 rounded-lg bg-primary-gradient flex items-center justify-center shadow-glow">
            <Rocket className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold tracking-tight text-lg">Three Day Launch<sup className="text-xs ml-0.5">®</sup></span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-white/80">
          <a href="#who" className="hover:text-white uppercase">WHO THIS IS FOR</a>
          <a href="#how" className="hover:text-white uppercase">HOW IT WORKS</a>
          <a href="#faq" className="hover:text-white">FAQ</a>
          <Link to="/contact" className="hover:text-white uppercase">CONTACT</Link>
        </nav>
        <Button asChild variant="default" className="ml-auto bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold rounded-full px-5">
          <a href={bookingUrl}>Launch a Website You Love</a>
        </Button>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section
      className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28"
      style={{
        backgroundImage: `url(${heroPeople})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/60" />

      <div className="relative mx-auto max-w-7xl px-6 grid md:grid-cols-2 gap-12 items-center">
        <div className="text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider border border-white/15">
            <Sparkles className="h-3.5 w-3.5 text-highlight" />
            Custom design. No templates.
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl leading-[1.1] font-bold">
            Modern responsive web <span className="text-primary">design.</span>
            <br />
            Built in just <span className="text-highlight">3 days.</span>
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-xl">
            Fast, custom websites for business owners. Get a conversion ready website you can show off with confidence.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Custom designed, never a template",
              "Launched in 3 days, not 3 months",
              "Complete, 4 Page Website",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/90">
                <span className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="h-3.5 w-3.5 text-primary" />
                </span>
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
              <a href={bookingUrl}>Book a Free Call to Get Started <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>
        </div>

        <LeadCard />
      </div>
    </section>
  );
}

function FounderVideo() {
  return (
    <section id="designer" className="py-24 bg-background pt-[45px] pb-[45px] scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            Meet your designer
          </div>
        </div>

        <div className="grid md:grid-cols-[1.6fr_1fr] gap-8 items-center">
          <div className="relative rounded-3xl overflow-hidden shadow-card-soft bg-secondary aspect-video">
            <video
              className="w-full h-full object-cover"
              controls
              poster={heroVideoPoster}
              preload="metadata"
            >
              <source src={heroVideo} type="video/mp4" />
            </video>
          </div>

          <div className="bg-card rounded-3xl border border-border p-6 shadow-card-soft">
            <div className="relative flex items-center gap-4">
              <div className="h-24 w-24 shrink-0 rounded-full overflow-hidden bg-muted shadow-card-soft">
                <img
                  src={sierraHeadshot}
                  alt="Sierra Janisse, THREE DAY LAUNCH FOUNDER"
                  width={384}
                  height={384}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-background rounded-2xl border border-border shadow-card-soft px-4 py-3 -ml-6">
                <div className="text-lg leading-tight">Sierra Janisse</div>
                <div className="text-primary font-semibold text-[11px] uppercase tracking-wider mt-0.5">
                  THREE DAY LAUNCH FOUNDER
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Button
            asChild
            size="lg"
            className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
          >
            <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Portfolio() {
  const projects = [
    { img: portfolioSpeakingAuthor, name: "Speaking + Author", tag: "Keynote site" },
    { img: portfolioSeniorTransportation, name: "Senior Transportation", tag: "Service site" },
    { img: portfolioCatering, name: "Catering", tag: "Event bookings" },
    { img: portfolioDogGrooming, name: "Dog Grooming", tag: "Booking-driven" },
    { img: portfolioFinancialAdvisor, name: "Financial Advisor", tag: "Lead capture" },
    { img: portfolioPhysicalTherapist, name: "Physical Therapist", tag: "Lead capture" },
    { img: portfolioMedSpa, name: "Med Spa", tag: "Booking-driven" },
    { img: portfolioCoffeeShop, name: "Coffee Shop", tag: "Local business" },
    { img: portfolioLandscaping, name: "Landscaping", tag: "Quote requests" },
    { img: portfolioAccounting, name: "Accounting", tag: "Advisory firm" },
    { img: portfolioKeynotes, name: "Keynote Speaker", tag: "Booking inquiries" },
    { img: portfolioHairSalon, name: "Hair Salon", tag: "Booking-driven" },
    
  ];
  const [showAllProjects, setShowAllProjects] = useState(false);
  return (
    <section id="jealous" className="py-24 bg-secondary text-secondary-foreground scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="mt-4 text-4xl md:text-5xl text-white font-bold">
            Your old website is <span className="text-primary">already jealous.</span>
          </h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {projects.map((p, i) => (
            <div key={p.name} className={`group ${i >= 4 && !showAllProjects ? "hidden md:block" : ""}`}>
              <div className="relative">
                <div className="relative bg-[#1a1a1a] rounded-t-2xl p-2 pt-6 border border-white/10">
                  <div className="flex gap-1.5 absolute top-2.5 left-4">
                    <span className="h-2 w-2 rounded-full bg-red-400/80" />
                    <span className="h-2 w-2 rounded-full bg-yellow-400/80" />
                    <span className="h-2 w-2 rounded-full bg-green-400/80" />
                  </div>
                  <div className="portfolio-scroll relative h-[280px] overflow-hidden rounded-lg bg-white">
                    <img
                      src={p.img}
                      alt={`${p.name} website design preview`}
                      width={1280}
                      height={1920}
                      loading="lazy"
                      className="portfolio-scroll-img w-full block"
                    />
                  </div>
                </div>
                <div className="mx-auto h-3 max-w-full bg-gradient-to-b from-[#2a2a2a] to-[#0f0f0f] rounded-b-2xl" style={{ width: "108%" }} />
                <div className="mx-auto h-1 bg-black/60 rounded-b-full" style={{ width: "30%" }} />
              </div>
              <div className="mt-5">
                <div className="text-white text-lg uppercase">{p.name}</div>
              </div>
            </div>
          ))}
        </div>
        {!showAllProjects && projects.length > 4 && (
          <div className="mt-8 text-center md:hidden">
            <Button variant="link" onClick={() => setShowAllProjects(true)} className="text-secondary-foreground underline underline-offset-4 p-0 h-auto text-sm font-medium">
              Load More Designs
            </Button>
          </div>
        )}
        <div className="mt-14 text-center">
          <Button
            asChild
            size="lg"
            className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
          >
            <a href={bookingUrl}>Launch a Website You Love <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function LeadCard() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const script = document.createElement("script");
    script.src = "https://sierra-at-three-day-launch.kit.com/4d152f9a64/index.js";
    script.async = true;
    script.setAttribute("data-uid", "4d152f9a64");
    containerRef.current.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);

  return (
    <div className="relative bg-card text-card-foreground rounded-3xl p-8 shadow-card-soft border border-border/40">
      <div className="absolute -top-4 left-8 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
        Claim your 3 Day Launch® site
      </div>
      <h2 className="text-2xl font-bold mt-2">Let's launch your website.</h2>
      <p className="text-muted-foreground text-sm mt-1">Give me the details:</p>
      <style>{`
        .formkit-powered-by-convertkit { display: none !important; }
        form[data-sv-form] button,
        form[data-sv-form] .formkit-submit {
          background-color: #000000;
          color: #ffffff;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-radius: 999px;
          padding: 14px 28px;
          border: none;
          font-size: 14px;
          transition: all 0.25s ease;
          cursor: pointer;
        }
        form[data-sv-form] button:hover,
        form[data-sv-form] .formkit-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(0,0,0,0.15);
          background-color: #111111;
        }
      `}</style>
      <div ref={containerRef} className="mt-6" />
    </div>
  );
}

function LogoStrip() {
  const items = [
    { quote: "OMG I LOVEEEEE my new site!!!", name: "Rachel" },
    { quote: "This site looks boss.", name: "Dave" },
    { quote: "Just booked my first gig from the website!", name: "Beatrice" },
    { quote: "Collaborators are impressed with what we have to offer.", name: "Jenni" },
    { quote: "This site surpasses my wildest dreams.", name: "Beth" },
  ];
  return (
    <div data-drag-marquee className="bg-secondary text-secondary-foreground py-6 overflow-hidden border-y border-white/5">
      <div className="flex gap-16 marquee whitespace-nowrap text-sm font-semibold tracking-wide text-white/70">
        {[...items, ...items, ...items].map((it, i) => (
          <span key={i} className="flex items-center gap-16">
            <span className="flex flex-col items-start leading-tight">
              <span className="italic">"{it.quote}"</span>
              <span className="text-xs text-white/70 normal-case mt-0.5">{it.name}</span>
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-primary/60" />
          </span>
        ))}
      </div>
    </div>
  );
}

function DollarSection() {
  return (
    <section className="relative bg-background overflow-hidden py-[45px]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative rounded-3xl bg-hero-gradient text-white p-10 md:p-16 overflow-hidden shadow-card-soft">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-highlight/20 blur-3xl" />

          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/30 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                WHAT YOU'RE LOOKING FOR
              </div>
              <h2 className="mt-4 text-4xl md:text-6xl font-bold leading-tight">
                Attract & <span className="text-primary">convert</span> clients and customers
              </h2>
              <p className="mt-5 text-white/75 text-lg max-w-xl">
                You shouldn't have to wait for weeks or pay thousands for a website that works.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                {[
                  { icon: Zap, label: "Live in 3 days" },
                  { icon: Palette, label: "100% custom design" },
                  { icon: ShieldCheck, label: "Strategic, multi-page sites" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="text-sm font-semibold">{label}</span>
                  </div>
                ))}
              </div>
              <Button asChild size="lg" className="mt-8 bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
                <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
            </div>

            <div className="relative">
              <div className="bg-white text-foreground rounded-3xl p-8 pb-12 shadow-2xl rotate-2">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">THE SIGNATURE</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-7xl font-bold text-primary">3 Day Launch®</span>
                </div>
                <div className="mt-4 h-px bg-border" />
                <ul className="mt-4 space-y-2 text-sm">
                  {["100% Ownership Rights", "Mobile Responsive Website", "SSL Certificate (HTTPS)", "SEO (Search Engine Optimization) ", "Live In 3 Days"].map(i => (
                    <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {i}</li>
                  ))}
                </ul>
              </div>


            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoFor() {
  return (
    <section id="who" className="py-24 bg-muted/40 overflow-hidden pt-[45px]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            Who is this for?
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold">
            If you sell a service <span className="text-primary">this is for you.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Online or in-person, big or solo. If your business runs on bookings,
            quotes, or calls, I'll build the site that brings them in.
          </p>
        </div>
      </div>

      <div data-drag-marquee className="mt-14 relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <div className="marquee flex w-max gap-4 will-change-transform">
          {[...businessTypes, ...businessTypes].map(({ icon: Icon, name }, i) => (
            <div key={`${name}-${i}`} className="group shrink-0 w-48 sm:w-64 bg-card rounded-2xl p-5 sm:p-6 border border-border hover:border-primary/40 hover:shadow-card-soft transition-all">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
              </div>
              <div className="mt-4 font-semibold text-sm sm:text-base">{name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6">
        <p className="text-center mt-10 text-sm text-muted-foreground">
          …and dozens more. If clients look you up before buying, you're in the right place.
        </p>
        <div className="mt-6 flex justify-center">
          <Button
            asChild
            size="lg"
            className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
          >
            <a href={bookingUrl}>
              Let's Launch Your Website <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function HowAffordable() {
  return (
    <section id="how" className="py-24 bg-background">
      <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            RELIABLE SUPPORT
          </div>
          <h2 className="mt-4 text-2xl md:text-3xl font-bold leading-tight">
            Simple. We're willing to work for <span className="text-foreground">practically free to prove ourselves to you.</span>
          </h2>
          <p className="mt-5 text-muted-foreground text-lg">
            I want to be your go-to website gal not just for your initial build, but for the long haul.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { t: "Lightning Fast Timelines", d: "Days instead of months, so you can start seeing ROI from your new website asap." },
              { t: "Custom Quality You Can Trust", d: "Every pixel designed for your business. Never a template." },
              
            ].map(({ t, d }) => (
              <div key={t} className="flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-primary-gradient flex items-center justify-center flex-shrink-0 shadow-glow">
                  <Check className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-bold">{t}</div>
                  <div className="text-muted-foreground text-sm">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="bg-secondary text-secondary-foreground rounded-3xl p-10 shadow-card-soft">
            <div className="text-xs font-bold uppercase tracking-wider text-white/80">The typical agency experience</div>
            <div className="mt-2 text-5xl font-bold line-through text-white/70">3–6 Months</div>
            <div className="mt-1 text-sm text-white/60">Endless revisions. Cookie-cutter template. You're just another ticket in the queue.</div>
            <div className="mt-8 h-px bg-white/10" />
            <div className="mt-6 text-xs font-bold uppercase tracking-wider text-primary">THE THREE DAY LAUNCH WAY</div>
            <div className="mt-2 text-4xl md:text-5xl font-bold text-primary whitespace-nowrap">Live in 3 Days</div>
            <div className="mt-1 text-sm text-white/70">A multi-page, custom website designed just for your business. Never a template.</div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-12 text-center">
        <Button
          asChild
          size="lg"
          className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
        >
          <a href={bookingUrl}>Get A Website That WORKS</a>
        </Button>
      </div>
    </section>
  );
}

function MeetDesigner() {
  return (
    <section id="meet-designer" className="py-24 bg-background scroll-mt-24 pt-[0px]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            Meet Your Designer
          </div>
        </div>

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14 items-center">
          <div className="relative rounded-3xl overflow-hidden shadow-card-soft bg-muted">
            <img
              src={sierraStairs}
              alt="Sierra Janisse, THREE DAY LAUNCH FOUNDER"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              Sierra Janisse
            </h2>
            <div className="mt-2 text-primary font-semibold text-sm uppercase tracking-wider">
              THREE DAY LAUNCH FOUNDER
            </div>

            <div className="mt-6 space-y-5 text-muted-foreground text-lg leading-relaxed">
              <p>
                I've launched hundreds of websites for businesses across every industry,
                from coaches and consultants to interior designers and hair stylists to
                accountants and dog groomers.
              </p>
              <p>
                I founded Three Day Launch because I believe that every business owner
                deserves a website they're proud of. There is absolutely no reason for
                good websites to cost thousands of dollars or take months to happen.
                Here's to your website being stunning and EASY.
              </p>
            </div>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
              >
                <a href={bookingUrl}>Launch a Website You Love <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "01", t: "Meet over Zoom for your Strategy Call.", d: "One on one with your designer to get clear on your vision and website goals" },
    { n: "02", t: "I design your custom website, all in one day.", d: "No meetings this day, you will just need to be available to provide feedback notes as I'll be making revisions for you all on this day" },
    { n: "03", t: "Domain connection & launch celebration.", d: "We hit publish, I connect your domain (like yourbusiness.com) for you, and you're officially online" },
  ];
  return (
    <section id="process" className="py-24 bg-muted/40">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold">Three days, <span className="text-primary">and you're live</span></h2>
          <p className="mt-4 text-muted-foreground text-lg">From "let's do it" to "we're live" in less time than most designers take to send a proposal.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-card rounded-3xl p-8 border border-border shadow-card-soft">
              <div className="text-6xl font-bold text-primary/50">{s.n}</div>
              <h3 className="mt-2 text-xl font-bold">{s.t}</h3>
              <p className="mt-3 text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Button
            asChild
            size="lg"
            className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
          >
            <a href={bookingUrl}>Get A Website That WORKS</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function ThenWhat() {
  return (
    <section className="relative py-24 overflow-hidden bg-secondary text-secondary-foreground">
      {/* Decorative background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, color-mix(in oklab, var(--primary) 35%, transparent) 0%, transparent 45%), radial-gradient(circle at 85% 80%, color-mix(in oklab, var(--primary) 25%, transparent) 0%, transparent 50%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div aria-hidden="true" className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-primary/30 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />

      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
          we've got your back
        </div>
        <h2 className="mt-5 text-4xl md:text-5xl font-bold tracking-tight text-white">
          Three days for my full website build... <span className="text-primary">then what?</span>
        </h2>
        <div className="mt-8 space-y-5 text-lg text-white/75 text-left md:text-center">
          <p>
            We don't just build you a beautiful website and then ditch you to "figure it out" post launch.
            After your website build is complete, you'll choose your preferred website monthly care plan.
          </p>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    {
      q: "What types of websites do you design?",
      a: "I've launched hundreds of successful websites for business owners across industries like consulting and finance to dog grooming and interior design! \n\nIf you sell a service and need a professional online presence so clients or customers can learn more about you online, this is for you. \n\nThis is NOT for you if you need an e-commerce website.  \n\nI do not design website for e-commerce so if you're looking to sell products online (like Amazon) this isn't a good fit for you. ",
    },
    {
      q: "Do you really launch in 3 days?",
      a: "Yes. Day one is your kick off call where we meet over Zoom and chat face to face to get your project started. \n\nDay two is design day. No live calls this day, but you will need to be available to provide feedback on the designs as I create them for you. By the end of day two, your design is DONE!\n\nDay three I connect your domain (like yourbusiness.com) and set up hosting for you so that you're officially live. It's celebration time!",
    },
    {
      q: "What about my website domain?",
      a: "You will need to purchase your website domain (like mybusiness.com) separately from a domain registrar. We recommend GoDaddy because it's easy, and only about $20 per year for your domain.",
    },
    {
      q: "What about hosting?",
      a: "We'll take care of hosting setup for you as part of your launch so you never have to worry about keeping your site online.",
    },
    {
      q: "Do you help with ongoing updates and maintenance long term?",
      a: "Yes! We don't just build you a beautiful website and leave you to figure it out post-launch. After your site is designed, you'll have your choice of ongoing website care plans.",
    },
  ];
  return (
    <section id="faq" className="bg-background py-0 pb-[90px]">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            FAQ
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold">Questions? I've got answers.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`i-${i}`} className="bg-card rounded-2xl border border-border px-6 data-[state=open]:shadow-card-soft">
              <AccordionTrigger className="text-left font-semibold text-base hover:no-underline">{item.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground whitespace-pre-line">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12 text-center">
          <Button
            asChild
            size="lg"
            className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
          >
            <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section id="contact" className="py-20 bg-secondary text-secondary-foreground">
      <div className="mx-auto max-w-5xl px-6 text-center">
        <h2 className="text-4xl md:text-6xl font-bold">
          Your website. <span className="text-primary">Live in 3 days.</span>
        </h2>
        <p suppressHydrationWarning className="mt-5 text-white/70 text-lg max-w-xl mx-auto">
Let's build a website that grows your business.
          <br /><br />
          Get started by scheduling a free, no-pressure call.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
          <Button asChild size="lg" variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white h-14 px-8 rounded-full text-base">
            <a href={bookingUrl}>Talk to a human</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10">
      <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
            <Rocket className="h-4 w-4 text-white" />
          </div>
          <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
        </div>
        <nav className="flex items-center gap-6 text-sm text-white/70">
          <Link to="/about" className="hover:text-white transition-colors">About</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
        </nav>
        <div className="text-sm text-white/80">© {new Date().getFullYear()} Three Day Launch. Custom websites for business owners.</div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-4 text-center text-sm text-white/80">
        Based in Chicago | Serving Clients Worldwide
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-2 text-center text-xs text-white/60">
        Three Day Launch is an independent website design studio creating professional websites.
      </div>
    </footer>
  );
}

const testimonials = [
  {
    quote: "Always pleasant, upbeat, and open to feedback, making the process smooth and enjoyable. Your creativity and attention to detail really shone through, and you were able to translate my ideas into a beautiful, functional website.",
    name: "Elaine Espada",
    avatar: avatarElaine,
  },
  {
    quote: "You taught me more than I had previously learned from paying two other business coaches. And the experience of working together was fun!",
    name: "Tarrn MacCarthy",
    avatar: avatarTarrn,
  },
  {
    quote: "Absolutely amazing. Sierra really cares about her clients and helps them achieve their goals. You will not regret working with her.",
    name: "Lissa Appiah",
    avatar: avatarLissa,
  },
  {
    quote: "Now, this website feels like ME — Like people can actually feel who I am and see what I'm up to! YOU TOTALLY ROCK!",
    name: "Ahriana Platten",
    avatar: avatarAhriana,
  },
  {
    quote: "I love how you asked great questions and had creative ways of figuring out what we wanted. The end result really shows that part of us. You helped us send the message we were hoping to send.",
    name: "Carrie St. Clair",
    avatar: avatarCarrie,
  },
];

function Testimonials() {
  const loop = [...testimonials, ...testimonials];
  return (
    <section id="testimonials" className="py-24 bg-background overflow-hidden scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold">
            Don't take our word for it,
          </h2>
          <p className="mt-2 text-4xl md:text-5xl font-bold text-primary">take theirs:</p>
        </div>
      </div>
      <div className="group relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <div className="flex w-max gap-6 animate-marquee group-hover:[animation-play-state:paused]">
          {loop.map((t, i) => (
            <figure
              key={`${t.name}-${i}`}
              className="w-[340px] md:w-[400px] shrink-0 rounded-2xl bg-card border border-border p-8 flex flex-col shadow-sm"
            >
              <div className="text-primary text-8xl leading-none font-black mb-2" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>“</div>
              <blockquote className="text-foreground/80 leading-relaxed flex-1">
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="h-12 w-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div className="font-semibold">{t.name}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-16 text-center">
        <Button
          asChild
          size="lg"
          className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
        >
          <a href={bookingUrl}>Start Today</a>
        </Button>
      </div>
    </section>
  );
}
